import React, { useEffect, useState } from "react";
import BunkeyApi from "../../api/BunkeyAPI";

const formatDate = value => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month} · ${hours}:${minutes}`;
};

const formatDueDate = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

const formatValue = (snapshot, fallback, intl) => {
  if (!snapshot) return fallback || "—";
  const status = snapshot.rightsStatus || snapshot.status;
  const dueDate = snapshot.dueDate;
  if (!status && !dueDate) return fallback || "—";

  let label = status || "—";
  if (status === "unassigned") {
    label = intl.formatMessage({ id: "rights_state_unassigned" });
  } else if (status === "enabled") {
    label = dueDate
      ? (new Date(dueDate).getTime() < Date.now()
        ? intl.formatMessage({ id: "rights_state_expired" })
        : intl.formatMessage({ id: "rights_state_valid" }))
      : intl.formatMessage({ id: "rights_state_valid" });
  }

  return `${label}${dueDate ? ` · ${formatDueDate(dueDate)}` : ""}`;
};

const formatOperation = (entry, intl) => {
  const operation = entry.action || entry.operation;
  if (operation === "single") {
    return intl.formatMessage({ id: "rights_history_operation_single" });
  }
  if (operation === "batch" || operation === "bulk" || operation === "lote") {
    return intl.formatMessage({ id: "rights_history_operation_batch" });
  }
  return operation || intl.formatMessage({ id: "rights_history_change" });
};

function RightsHistory({ itemSelected, workclient, user, modules, keyCollection, intl }) {
  const [state, setState] = useState({ loading: false, error: false, entries: [] });
  const isSharedPage = keyCollection === "assetsPublicShared";
  const useRightsActive = Boolean(
    modules && modules.some(module => module.name === "useRights" && module.activate)
  );

  useEffect(() => {
    if (isSharedPage || !user || !itemSelected || !itemSelected._id || !useRightsActive) {
      setState({ loading: false, error: false, entries: [] });
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true, error: false, entries: [] });

    BunkeyApi({
      url: `/clients/${workclient}/rights/assets/${itemSelected._id}/log?userid=${user._id}&page=1&limit=20`,
      method: "GET"
    })
      .then(resp => {
        if (!cancelled) {
          setState({
            loading: false,
            error: false,
            entries: Array.isArray(resp.data) ? resp.data : []
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, entries: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [itemSelected && itemSelected._id, workclient, user && user._id, isSharedPage, useRightsActive]);

  if (isSharedPage || !user || !useRightsActive) return null;

  return (
    <div className="rights-detail-history rights-detail-history--left-column css-color-bunkers">
      <div className="rights-detail-history__title">
        {intl.formatMessage({ id: "rights_history_title" })}
        {!state.loading && !state.error && ` · ${state.entries.length}`}
      </div>
      {state.loading && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_loading" })}
        </div>
      )}
      {!state.loading && state.error && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_error" })}
        </div>
      )}
      {!state.loading && !state.error && state.entries.length === 0 && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_empty" })}
        </div>
      )}
      {!state.loading && !state.error && state.entries.map((entry, index) => (
        <div className="rights-detail-history__entry" key={entry._id || entry.operationId || index}>
          <div className="rights-detail-history__meta">
            {formatDate(entry.createdAt || entry.createdDate || entry.at)}
          </div>
          <div className="rights-detail-history__what">
            <div className="rights-detail-history__action">
              <b>{entry.username || entry.rightsAssignedByName || "—"}</b>
              {` — ${formatOperation(entry, intl)}`}
              {(entry.affectedCount || entry.count) && ` sobre ${entry.affectedCount || entry.count} piezas`}
            </div>
            <div className="rights-detail-history__change">
              <span className="rights-detail-history__was">
                {formatValue(entry.previous, intl.formatMessage({ id: "rights_history_no_previous" }), intl)}
              </span>
              <span aria-hidden="true"> → </span>
              <span className="rights-detail-history__now">
                {formatValue(entry.next, intl.formatMessage({ id: "rights_history_no_next" }), intl)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default RightsHistory;
